import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, CheckSquare, Save, Coins, Clock, CheckCircle2, User } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/packages")({
  head: () => ({ meta: [{ title: "J Paketler — JUËN Admin" }] }),
  component: AdminPackagesPage,
});

interface PackageRow {
  id: string;
  name: string;
  points: number;
  price_kurus: number;
  shopier_payment_url: string | null;
  sort_order: number;
  is_active: boolean;
}

interface PackageOrderRow {
  id: string;
  user_id: string;
  package_id: string;
  points: number;
  price_kurus: number;
  status: string;
  admin_notes: string | null;
  created_at: string;
  marked_paid_at: string | null;
  profiles?: { first_name: string; last_name: string; email: string } | null;
  point_packages?: { name: string } | null;
}

type Filter = "pending" | "paid";

function AdminPackagesPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [orders, setOrders] = useState<PackageOrderRow[]>([]);
  const [filter, setFilter] = useState<Filter>("pending");
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const loadPackages = useCallback(async () => {
    const { data } = await supabase.from("point_packages").select("*").order("sort_order");
    setPackages((data as PackageRow[]) ?? []);
  }, []);

  const loadOrders = useCallback(async () => {
    const { data: rows } = await supabase
      .from("point_package_orders")
      .select("*")
      .eq("status", filter)
      .order("created_at", { ascending: false })
      .limit(200);
    const list = (rows as PackageOrderRow[]) ?? [];
    const userIds = [...new Set(list.map((r) => r.user_id))];
    const pkgIds = [...new Set(list.map((r) => r.package_id))];
    const [{ data: profs }, { data: pkgs }] = await Promise.all([
      userIds.length
        ? supabase.from("profiles").select("id,first_name,last_name,email").in("id", userIds)
        : Promise.resolve({ data: [] as { id: string; first_name: string; last_name: string; email: string }[] }),
      pkgIds.length
        ? supabase.from("point_packages").select("id,name").in("id", pkgIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[] }),
    ]);
    const profMap = new Map((profs ?? []).map((p) => [p.id, p]));
    const pkgMap = new Map((pkgs ?? []).map((p) => [p.id, p]));
    setOrders(
      list.map((r) => ({
        ...r,
        profiles: profMap.get(r.user_id) ?? null,
        point_packages: pkgMap.get(r.package_id) ?? null,
      })),
    );
  }, [filter]);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      await Promise.all([loadPackages(), loadOrders()]);
      setPageLoading(false);
    })();
  }, [isAdmin, loadPackages, loadOrders]);

  if (loading || !isAdmin || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">J Paketler</h1>
          <p className="mt-2 text-muted-foreground">
            J paketlerini ve Shopier ödeme bağlantılarını yönet, ödemeleri onayla.
          </p>
        </div>

        <AdminTabs />

        {/* Packages list */}
        <h2 className="mb-3 text-lg font-semibold">Paketler</h2>
        <div className="space-y-3">
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} onSaved={loadPackages} />
          ))}
        </div>

        {/* Orders */}
        <h2 className="mb-3 mt-10 text-lg font-semibold">Paket siparişleri</h2>

        <div className="mb-6 flex gap-2 rounded-xl border border-border bg-card p-1 shadow-card">
          {([
            { key: "pending" as const, label: "Bekleyen", icon: Clock },
            { key: "paid" as const, label: "Onaylı", icon: CheckCircle2 },
          ]).map((t) => {
            const Icon = t.icon;
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {orders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Bu kategoride sipariş yok.
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} onChanged={loadOrders} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PackageCard({ pkg, onSaved }: { pkg: PackageRow; onSaved: () => void }) {
  const [name, setName] = useState(pkg.name);
  const [points, setPoints] = useState(String(pkg.points));
  const [priceTl, setPriceTl] = useState(String(pkg.price_kurus / 100));
  const [url, setUrl] = useState(pkg.shopier_payment_url ?? "");
  const [active, setActive] = useState(pkg.is_active);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("point_packages")
      .update({
        name: name.trim(),
        points: Math.max(1, Math.floor(Number(points) || 0)),
        price_kurus: Math.max(1, Math.floor(Number(priceTl) * 100 || 0)),
        shopier_payment_url: url.trim() || null,
        is_active: active,
      })
      .eq("id", pkg.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Paket güncellendi");
    onSaved();
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-primary" />
          <span className="font-semibold">{pkg.name}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Aktif</span>
          <Switch checked={active} onCheckedChange={setActive} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Paket adı</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">J miktarı</Label>
          <Input type="number" value={points} onChange={(e) => setPoints(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Fiyat (₺)</Label>
          <Input type="number" value={priceTl} onChange={(e) => setPriceTl(e.target.value)} />
        </div>
      </div>

      <div className="mt-3">
        <Label className="text-xs">Shopier ödeme bağlantısı</Label>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.shopier.com/..."
        />
      </div>

      <Button onClick={save} disabled={saving} className="mt-4 w-full" size="sm">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Kaydet
      </Button>
    </div>
  );
}

function OrderCard({ order, onChanged }: { order: PackageOrderRow; onChanged: () => void }) {
  const [notes, setNotes] = useState(order.admin_notes ?? "");
  const [submitting, setSubmitting] = useState(false);

  const markPaid = async () => {
    if (!confirm(`Bu sipariş için Shopier'de ödeme alındığını onaylıyor musun?\n\nKullanıcının bakiyesine ${order.points} J otomatik yüklenecek.`)) {
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.rpc("admin_mark_package_order_paid", {
      _order_id: order.id,
      _notes: notes.trim() || undefined,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`+${order.points} J kullanıcının bakiyesine eklendi.`);
    onChanged();
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xs text-muted-foreground">#{order.id.slice(0, 8)}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(order.created_at).toLocaleString("tr-TR")}
            </span>
          </div>
          <h3 className="mt-1 text-lg font-bold">
            {order.points.toLocaleString("tr-TR")} J
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({(order.price_kurus / 100).toLocaleString("tr-TR")} ₺)
            </span>
          </h3>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {order.profiles && (
              <span className="inline-flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                {order.profiles.first_name} {order.profiles.last_name} · {order.profiles.email}
              </span>
            )}
            {order.point_packages && (
              <span>Paket: {order.point_packages.name}</span>
            )}
          </div>
          {order.admin_notes && (
            <div className="mt-2 rounded-lg bg-muted/50 p-2 text-xs">
              <span className="font-semibold">Not:</span> {order.admin_notes}
            </div>
          )}
        </div>
      </div>

      {order.status === "pending" && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Not (opsiyonel) — ör. Shopier işlem no, tarih..."
            rows={2}
          />
          <Button onClick={markPaid} disabled={submitting} className="w-full">
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckSquare className="mr-2 h-4 w-4" />
            )}
            Ödendi olarak işaretle & J yükle
          </Button>
        </div>
      )}
    </div>
  );
}
